/**
 * Which page the reader came from, and the one rule that reads it.
 *
 * Anand, 24 Sep 26: "ensure from nowhere, direct or referenced pages, /app
 * sends the user to /fly-app". The account area and the agent pages under
 * /app are the surface a signed-up owner lives on; /fly-app is the older Fly
 * app, which they should never be dropped into. Most of those links are
 * rewritten where they are drawn (rewriteLinkForApp), but a page deep inside
 * an embedded surface can still navigate on its own, so this is the net
 * underneath: a step from /app into /fly-app lands back on /app.
 *
 * It is a net, not a wall. Someone who opens /fly-app directly, or reaches it
 * from anywhere other than /app, gets the page as before, and an agency on
 * its own domain is never touched.
 *
 * Nothing here runs on the server: the pages are server-rendered on www, and
 * a module variable there would be shared by every request.
 */

const PREVIOUS_KEY = 'fly-route-previous';

let previous: string | null = null;
let current: string | null = null;

const isBrowser = () => typeof window !== 'undefined';

/** The page being shown now, with the one before it kept. */
export function rememberRoute(pathname: string): void {
  if (!isBrowser()) return;
  const next = String(pathname ?? '');
  if (!next || next === current) return;
  previous = current;
  current = next;
  try {
    if (previous) sessionStorage.setItem(PREVIOUS_KEY, previous);
    else sessionStorage.removeItem(PREVIOUS_KEY);
  } catch {
    /* private mode: the module variable still answers for this tab */
  }
}

/** The page the reader was on before this one, or null on a fresh load. */
export function previousRoute(): string | null {
  if (previous) return previous;
  if (!isBrowser()) return null;
  try {
    return sessionStorage.getItem(PREVIOUS_KEY);
  } catch {
    return null;
  }
}

/** Test seam: forget this tab's route memory. */
export function resetRouteMemory(): void {
  previous = null;
  current = null;
  try {
    if (isBrowser()) sessionStorage.removeItem(PREVIOUS_KEY);
  } catch {
    /* nothing stored, nothing to forget */
  }
}

/** True when a path is the account area or a location page inside it. */
function inApp(pathname: string | null): boolean {
  return pathname === '/app' || Boolean(pathname && pathname.startsWith('/app/'));
}

/** True when a path is the older Fly app. */
function inFlyApp(pathname: string): boolean {
  return pathname === '/fly-app' || pathname.startsWith('/fly-app/');
}

/**
 * Whether this step has to be turned around: someone inside /app who has just
 * been sent to /fly-app. An agency on its own domain keeps its whole app, so
 * it is never gated.
 */
export function shouldGateFlyApp(previousPath: string | null, currentPath: string, agencyDomain: boolean): boolean {
  if (agencyDomain) return false;
  if (!inFlyApp(String(currentPath ?? ''))) return false;
  return inApp(previousPath);
}
